/**
 * OutputFormatsSubTab — Template list view for a given sourceType.
 * Renders inside AccountingConfigPanel as a sub-tab.
 * Toggles between list view and editor view via local state.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { outputFormatsService } from '../../services/api/outputFormatsService';
import { dataFeedService } from '../../services/api/dataFeedService';
import OutputFormatsTemplateEditor from './OutputFormatsTemplateEditor';
import AlertModal from '../common/AlertModal';
import ApiKeyModal from '../common/ApiKeyModal';
import type {
  SourceType,
  OutputFormatTemplateSummary,
} from '../../types/outputFormats';
import type { ApiKeyDocument } from '../../types/apiKey';
import './OutputFormats.css';

interface OutputFormatsSubTabProps {
  clientId: string;
  sourceType: SourceType;
}

const OutputFormatsSubTab: React.FC<OutputFormatsSubTabProps> = ({
  clientId,
  sourceType,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'client_admin';

  // List state
  const [templates, setTemplates] = useState<OutputFormatTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Navigation state: list view vs editor view
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Duplicate in-progress
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  // Alert modal
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' });

  // API Key state (KAN-93)
  const [apiKeyMap, setApiKeyMap] = useState<Record<string, ApiKeyDocument>>({});
  const [apiKeyModal, setApiKeyModal] = useState<{
    isOpen: boolean;
    templateId: string;
    templateName: string;
    existingKey?: ApiKeyDocument | null;
  }>({ isOpen: false, templateId: '', templateName: '' });

  const showSuccess = (message: string) => {
    setAlertModal({ isOpen: true, type: 'success', title: t('common.success'), message });
  };

  const showError = (message: string) => {
    setAlertModal({ isOpen: true, type: 'error', title: t('common.error'), message });
  };

  // Load templates
  const loadTemplates = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const promises: [Promise<OutputFormatTemplateSummary[]>, Promise<ApiKeyDocument[]> | Promise<never[]>] = [
        outputFormatsService.listTemplates(clientId, sourceType),
        isAdmin ? dataFeedService.listKeys(clientId) : Promise.resolve([]),
      ];
      const [data, apiKeys] = await Promise.all(promises);
      setTemplates(data);
      // Build templateId → API key map (admin only)
      const km: Record<string, ApiKeyDocument> = {};
      apiKeys.forEach(k => { km[k.templateId] = k; });
      setApiKeyMap(km);
    } catch (err) {
      console.error('Error loading output format templates:', err);
      setError(t('outputFormats.loadError'));
    } finally {
      setLoading(false);
    }
  }, [clientId, sourceType, isAdmin, t]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Handlers
  const handleNewTemplate = () => {
    setIsCreating(true);
    setEditingTemplateId(null);
  };

  const handleEditTemplate = (templateId: string) => {
    setEditingTemplateId(templateId);
    setIsCreating(false);
  };

  const handleDuplicate = async (templateId: string) => {
    setDuplicatingId(templateId);
    try {
      await outputFormatsService.duplicateTemplate(clientId, templateId);
      showSuccess(t('outputFormats.duplicateSuccess'));
      await loadTemplates();
    } catch (err) {
      console.error('Error duplicating template:', err);
      showError(String(err));
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleEditorSave = () => {
    setEditingTemplateId(null);
    setIsCreating(false);
    loadTemplates();
  };

  const handleEditorCancel = () => {
    setEditingTemplateId(null);
    setIsCreating(false);
  };

  // API Key handlers (KAN-93)
  const handleOpenApiKeyModal = (templateId: string, templateName: string) => {
    const existing = apiKeyMap[templateId] || null;
    setApiKeyModal({ isOpen: true, templateId, templateName, existingKey: existing });
  };

  const handleApiKeyChanged = useCallback(async () => {
    // Lightweight refresh — only reload API keys, no loading spinner
    if (!clientId || !isAdmin) return;
    try {
      const apiKeys = await dataFeedService.listKeys(clientId);
      const km: Record<string, ApiKeyDocument> = {};
      apiKeys.forEach(k => { km[k.templateId] = k; });
      setApiKeyMap(km);
    } catch (err) {
      console.error('Error refreshing API keys:', err);
    }
  }, [clientId, isAdmin]);

  // If in editor mode, render the editor
  if (isCreating || editingTemplateId) {
    return (
      <OutputFormatsTemplateEditor
        clientId={clientId}
        sourceType={sourceType}
        templateId={editingTemplateId ?? undefined}
        onSave={handleEditorSave}
        onCancel={handleEditorCancel}
      />
    );
  }

  // Sort: active first, then by name
  const sortedTemplates = [...templates].sort((a, b) => {
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  // List view
  return (
    <div className="output-formats-sub-tab">
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />

      <div className="of-toolbar">
        <h3>{t('outputFormats.pageTitle')}</h3>
        <button className="btn-primary btn-small" onClick={handleNewTemplate}>
          + {t('outputFormats.newTemplate')}
        </button>
      </div>

      {loading && (
        <div className="tab-loading">
          <div className="spinner" />
          <p>{t('common.loading')}</p>
        </div>
      )}

      {error && (
        <div className="feedback-message error">{error}</div>
      )}

      {!loading && !error && sortedTemplates.length === 0 && (
        <div className="of-empty-state">
          <p>{t('outputFormats.noTemplates')}</p>
        </div>
      )}

      {!loading && !error && sortedTemplates.length > 0 && (
        <table className="template-list-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th>{t('outputFormats.fields.name')}</th>
              <th>{t('outputFormats.fields.outputFormat')}</th>
              <th>{t('outputFormats.fields.isActive')}</th>
              <th style={{ width: '250px' }}></th>
            </tr>
          </thead>
          <tbody>
            {sortedTemplates.map((tpl) => {
              const linkedKey = apiKeyMap[tpl.templateId];
              return (
                <React.Fragment key={tpl.templateId}>
                  <tr className={`template-row ${!tpl.isActive ? 'inactive' : ''}`}>
                    <td className="default-star-cell">
                      {tpl.isDefault && <span className="default-star" title={t('outputFormats.defaults.isDefaultBadge')}>&#9733;</span>}
                    </td>
                    <td className="template-name-cell">
                      {tpl.name}
                      {!tpl.isActive && (
                        <span className="inactive-label"> {t('outputFormats.defaults.inactiveLabel')}</span>
                      )}
                    </td>
                    <td>
                      <span className={`format-badge format-${tpl.outputFormat.toLowerCase()}`}>
                        {tpl.outputFormat}
                      </span>
                      {tpl.rowMode === 'GROUPED' && (
                        <span className="format-badge format-grouped">
                          {t('outputFormats.rowMode.groupedBadge')}
                        </span>
                      )}
                    </td>
                    <td>
                      {tpl.isActive ? t('common.active') : t('common.inactive')}
                    </td>
                    <td className="template-actions-cell">
                      <button
                        className="of-btn"
                        onClick={() => handleEditTemplate(tpl.templateId)}
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        className="of-btn"
                        onClick={() => handleDuplicate(tpl.templateId)}
                        disabled={duplicatingId === tpl.templateId}
                      >
                        {duplicatingId === tpl.templateId ? '...' : t('common.duplicate')}
                      </button>
                      {isAdmin && (
                        <button
                          className="of-btn of-api-key-btn"
                          onClick={() => handleOpenApiKeyModal(tpl.templateId, tpl.name)}
                          title={linkedKey
                            ? `${linkedKey.name} — ${linkedKey.isActive ? t('dataFeeds.status.active', 'Active') : t('dataFeeds.status.disabled', 'Disabled')}`
                            : t('dataFeeds.actions.generateKey', 'Generate API Key')}
                        >
                          &#128273;
                          {linkedKey
                            ? <span className={`of-api-key-dot ${linkedKey.isActive ? 'active' : 'disabled'}`} />
                            : <span className="of-api-key-label">API</span>
                          }
                        </button>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {/* API Key Modal (KAN-93) */}
      <ApiKeyModal
        isOpen={apiKeyModal.isOpen}
        onClose={() => setApiKeyModal(prev => ({ ...prev, isOpen: false }))}
        clientId={clientId}
        templateId={apiKeyModal.templateId}
        templateName={apiKeyModal.templateName}
        templateSource="OUTPUT_FORMAT"
        existingKey={apiKeyModal.existingKey}
        onKeyChanged={handleApiKeyChanged}
      />
    </div>
  );
};

export default OutputFormatsSubTab;
